import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Paper, List, ListItem, ListItemText,
  IconButton, Chip, Tooltip, Divider, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import GroupsIcon from '@mui/icons-material/Groups';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { reorderTeamMembers, moveTeamMember, setPods, TeamMember } from '../../store/slices/podsSlice';
import { setCategories } from '../../store/slices/categoriesSlice';
import { setEpics } from '../../store/slices/epicsSlice';
import { setSprintSettings } from '../../store/slices/sprintsSlice';
import { setEpicWeights, setStoryWeights } from '../../store/slices/healthConfigSlice';
import { setAllEpicStatuses, setAllStoryStatuses, StatusMap } from '../../store/slices/stateManagementSlice';
import {
  scenario1Pods,
  scenario1Categories,
  scenario1Epics,
  scenario1Stories,
  scenario1SprintForecastConfig,
  scenario1HealthConfig,
  scenario1SprintStateConfig,
} from '../../data/scenario1Data';
import InboxIcon from '@mui/icons-material/Inbox';
import { groupBorderColors, darkGroupBorderColors, DEFAULT_MEMBER_BORDER_LIGHT, DEFAULT_MEMBER_BORDER_DARK } from '../../theme';
import TeamMemberModal from '../../components/TeamMemberModal/TeamMemberModal';

const PodManagement: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pods = useAppSelector((state) => state.pods.pods);
  const unassignedMembers = useAppSelector((state) => state.pods.unassignedMembers);
  const theme = useTheme();
  const activeGroupBorderColors = theme.palette.mode === 'dark' ? darkGroupBorderColors : groupBorderColors;
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(
    () => (localStorage.getItem('podManagementViewMode') as 'list' | 'grid') || 'grid'
  );
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [selectedPodId, setSelectedPodId] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const sourcePodId = source.droppableId;
    const destPodId = destination.droppableId;

    if (sourcePodId === destPodId) {
      const members =
        sourcePodId === 'unassigned'
          ? Array.from(unassignedMembers)
          : Array.from(pods.find((p) => p.id === sourcePodId)?.teamMembers ?? []);
      if (!members.length) return;
      const [removed] = members.splice(source.index, 1);
      members.splice(destination.index, 0, removed);
      dispatch(reorderTeamMembers({ podId: sourcePodId, members }));
    } else {
      const member =
        sourcePodId === 'unassigned'
          ? unassignedMembers[source.index]
          : pods.find((p) => p.id === sourcePodId)?.teamMembers[source.index];
      if (!member) return;
      dispatch(moveTeamMember({ fromPodId: sourcePodId, toPodId: destPodId, member, destinationIndex: destination.index }));
    }
  };

  const openAddMember = (podId: string) => {
    setSelectedPodId(podId);
    setSelectedMember(null);
    setMemberModalOpen(true);
  };

  const openEditMember = (podId: string, member: TeamMember) => {
    setSelectedPodId(podId);
    setSelectedMember(member);
    setMemberModalOpen(true);
  };

  if (pods.length === 0 && unassignedMembers.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <GroupsIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No Pods Configured
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Get started by creating your first pod.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pod-management/new')}
        >
          Create New Pod
        </Button>
        <Button
          sx={{ marginTop: 5 }}
          variant="text"
          onClick={() => {
            const epicsWithStories = scenario1Epics.map((epic) => ({
              ...epic,
              stories: scenario1Stories.filter((s) => s.epicId === epic.id),
            }));
            dispatch(setCategories(scenario1Categories));
            dispatch(setPods(scenario1Pods));
            dispatch(setEpics(epicsWithStories));
            dispatch(setSprintSettings(scenario1SprintForecastConfig));
            dispatch(setEpicWeights(scenario1HealthConfig.epicWeights));
            dispatch(setStoryWeights(scenario1HealthConfig.storyWeights));
            dispatch(setAllEpicStatuses(scenario1SprintStateConfig.epicStatuses as StatusMap));
            dispatch(setAllStoryStatuses(scenario1SprintStateConfig.storyStatuses as StatusMap));
          }}
        >
          Load Demo Data
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          variant="contained"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pod-management/new')}
        >
          Pod
        </Button>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_e, val) => { if (val) { setViewMode(val); localStorage.setItem('podManagementViewMode', val); } }}
            aria-label="view mode"
          >
            <Tooltip title="List View">
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Grid View">
              <ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

        </Box>
      </Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={
            viewMode === 'grid'
              ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 3,
              }
              : { display: 'flex', flexDirection: 'column', gap: 3 }
          }
        >
          {pods.map((pod) => {
            const engineerCount = pod.teamMembers.filter((m) => m.group === 'Engineering').length;
            const calculatedVelocity = pod.avgPointsPerEngineer > 0
              ? pod.avgPointsPerEngineer * engineerCount
              : pod.defaultVelocity;
            const isOverride = pod.avgPointsPerEngineer > 0 && pod.defaultVelocity !== calculatedVelocity;
            const displayedVelocity = isOverride ? pod.defaultVelocity : calculatedVelocity;
            return (
              <Paper key={pod.id} elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Tooltip
                    title={pod.description || ''}
                    arrow
                    placement="top"
                    disableHoverListener={!pod.description}
                    disableFocusListener={!pod.description}
                    disableTouchListener={!pod.description}
                  >
                    <Chip
                      label={pod.name}
                      variant="outlined"
                      clickable
                      onClick={() => navigate(`/pod-management/${pod.id}/edit`)}
                      aria-label={`Edit pod ${pod.name}`}
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        height: 'auto',
                        py: 0.75,
                        borderWidth: 2,
                        cursor: 'pointer',
                        '& .MuiChip-label': { px: 1.5 },
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Add Team Member">
                    <IconButton size="small" color="primary" onClick={() => openAddMember(pod.id)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Engineering Velocity:</Typography>
                  <Tooltip
                    title={
                      isOverride
                        ? `Override: ${displayedVelocity} pts (calculated: ${calculatedVelocity} pts)`
                        : pod.avgPointsPerEngineer > 0
                          ? `${engineerCount} engineer${engineerCount !== 1 ? 's' : ''} × ${pod.avgPointsPerEngineer} pts each`
                          : ''
                    }
                  >
                    <Chip
                      label={`${displayedVelocity} pts`}
                      size="small"
                      color={isOverride ? 'warning' : 'default'}
                      variant={isOverride ? 'outlined' : 'filled'}
                      sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  </Tooltip>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Droppable droppableId={pod.id}>
                  {(provided) => (
                    <List
                      dense
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{ minHeight: 40 }}
                    >
                      {pod.teamMembers.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          No team members yet. Add one above.
                        </Typography>
                      )}
                      {pod.teamMembers.map((member, index) => (
                        <Draggable key={member.id} draggableId={member.id} index={index}>
                          {(dragProvided) => (
                            <ListItem
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              onClick={() => openEditMember(pod.id, member)}
                              sx={{
                                mb: 0.5,
                                borderRadius: 1,
                                borderLeft: `4px solid ${activeGroupBorderColors[member.group] || (theme.palette.mode === 'dark' ? DEFAULT_MEMBER_BORDER_DARK : DEFAULT_MEMBER_BORDER_LIGHT)}`,
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                              }}
                            >
                              <Box {...dragProvided.dragHandleProps} sx={{ mr: 1, cursor: 'grab', color: 'text.secondary' }}>
                                <DragIndicatorIcon fontSize="small" />
                              </Box>
                              <ListItemText
                                primary={member.name}
                                secondary={
                                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                    <Chip label={member.group} size="small" sx={{ fontSize: '0.65rem' }} />
                                    <Chip label={member.practice} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                  </Box>
                                }
                              />
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </Paper>
            );
          })}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InboxIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="h6" fontWeight={600} color="text.secondary">
                  Unassigned
                </Typography>
              </Box>
              <Tooltip title="Add Team Member">
                <IconButton size="small" color="primary" onClick={() => openAddMember('unassigned')}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Droppable droppableId="unassigned">
              {(provided) => (
                <List
                  dense
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ minHeight: 40 }}
                >
                  {unassignedMembers.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No unassigned team members. Drag members here or add one above.
                    </Typography>
                  )}
                  {unassignedMembers.map((member, index) => (
                    <Draggable key={member.id} draggableId={member.id} index={index}>
                      {(dragProvided) => (
                        <ListItem
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          onClick={() => openEditMember('unassigned', member)}
                          sx={{
                            mb: 0.5,
                            borderRadius: 1,
                            borderLeft: `4px solid ${activeGroupBorderColors[member.group] || (theme.palette.mode === 'dark' ? DEFAULT_MEMBER_BORDER_DARK : DEFAULT_MEMBER_BORDER_LIGHT)}`,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Box {...dragProvided.dragHandleProps} sx={{ mr: 1, cursor: 'grab', color: 'text.secondary' }}>
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <ListItemText
                            primary={member.name}
                            secondary={
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Chip label={member.group} size="small" sx={{ fontSize: '0.65rem' }} />
                                <Chip label={member.practice} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              </Box>
                            }
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </Paper>
        </Box>
      </DragDropContext>
      <TeamMemberModal
        open={memberModalOpen}
        podId={selectedPodId}
        member={selectedMember}
        onClose={() => setMemberModalOpen(false)}
      />
    </Box>
  );
};

export default PodManagement;
